<template>
  <div
    v-if="open"
    class="overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="msg-send-title"
    @keydown.esc.prevent.stop="onClose"
  >
    <div class="dialog">
      <header class="dialog-header">
        <h2
          id="msg-send-title"
          class="h2 m-0"
        >
          Nachricht senden
        </h2>
      </header>

      <form
        class="form"
        @submit.prevent="onSubmit"
      >
        <!-- Empfänger -->
        <div class="field">
          <label for="recipients">Empfänger-Auswahl</label>
          <select
            id="recipients"
            v-model="form.recipientUserIds"
            style="height: 66px;"
            multiple
            :aria-invalid="form.recipientUserIds.length === 0"
          >
            <option
              v-for="opt in recipientsOptionsNorm"
              :key="opt.userId"
              :value="opt.userId"
              :title="opt.email"
            >
              {{ opt.name }} — {{ opt.email }}
            </option>
          </select>
          <p class="help mt-0">
            Mindestens einen Empfänger auswählen.
          </p>
        </div>

        <!-- Betreff -->
        <div class="field">
          <label for="subject">Betreff</label>
          <input
            id="subject"
            v-model.trim="form.subject"
            type="text"
            autocomplete="off"
            :aria-invalid="!form.subject"
          >
        </div>

        <!-- Nachricht -->
        <div class="field">
          <label for="message">Nachricht</label>
          <textarea
            id="message"
            v-model="form.message"
            rows="12"
            style="height: 12em;"
            :aria-invalid="!form.message"
          />
        </div>

        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>
        <p
          v-if="success"
          class="notice notice-success mt-12"
          role="status"
        >
          {{ success }}
        </p>

        <div class="dialog-actions">
          <button
            type="button"
            class="btn"
            @click="onClose"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="isLoading"
          >
            <span
              v-if="isLoading"
              class="spinner"
              aria-hidden="true"
            />
            Senden
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import {computed, reactive, watch, ref} from 'vue';
import {communicationsApi} from '@/api/communications';

const props = defineProps({
  open: {type: Boolean, default: false},
  topic: {type: String, default: 'absence_email'}, // für weitere Fälle erweiterbar
  studentId: {type: Number, required: true},
  recipientsOptions: { // [{userId, name, email}]
    type: Array,
    default: () => []
  },
  preselectedRecipientUserIds: { // optional, Standard: alle
    type: Array,
    default: () => []
  },
  defaultSubject: {type: String, default: ''},
  defaultMessage: {type: String, default: ''},
});

const emit = defineEmits(['close', 'sent']);

const recipientsOptionsNorm = computed(() => {
  return (Array.isArray(props.recipientsOptions) ? props.recipientsOptions : [])
      .map(it => ({
        userId: Number(it.userId ?? it.id ?? 0),
        name: String(it.name ?? '').trim(),
        email: String(it.email ?? '').trim()
      }))
      .filter(it => it.userId > 0 && it.email);
});

const form = reactive({
  recipientUserIds: [],
  subject: '',
  message: ''
});

const isLoading = ref(false);
const error = ref('');
const success = ref('');

function initForm() {
  const pre = Array.isArray(props.preselectedRecipientUserIds) && props.preselectedRecipientUserIds.length
      ? props.preselectedRecipientUserIds
      : recipientsOptionsNorm.value.map(r => r.userId);
  form.recipientUserIds = pre;
  form.subject = props.defaultSubject || '';
  form.message = props.defaultMessage || '';
  error.value = '';
  success.value = '';
}

watch(
    () => props.open,
    (o) => {
      if (o) initForm();
    }
);
watch(
    () => [props.defaultSubject, props.defaultMessage, props.preselectedRecipientUserIds, props.recipientsOptions],
    () => {
      if (props.open) initForm();
    }
);

function onClose() {
  emit('close');
}

async function onSubmit() {
  error.value = '';
  success.value = '';

  if (!props.studentId || props.studentId <= 0) {
    error.value = 'Ungültiger Schüler.';
    return;
  }
  if (!form.subject.trim() || !form.message.trim()) {
    error.value = 'Betreff und Nachricht dürfen nicht leer sein.';
    return;
  }
  if (!form.recipientUserIds.length) {
    error.value = 'Bitte wählen Sie mindestens einen Empfänger aus.';
    return;
  }

  isLoading.value = true;
  try {
    // Aktuell: absence_email
    if (props.topic === 'absence_email') {
      await communicationsApi.sendAbsenceEmail({
        studentId: props.studentId,
        recipientUserIds: form.recipientUserIds,
        subject: form.subject,
        message: form.message
      });
    } else {
      // Platzhalter für zukünftige Topics
    }

    success.value = 'E-Mail wurde versendet.';
    emit('sent');
    setTimeout(() => emit('close'), 2000);
  } catch (e) {
    error.value = e?.response?.data?.message || 'Versand fehlgeschlagen.';
  } finally {
    isLoading.value = false;
  }
}
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 50;
}

.dialog {
  width: 100%;
  max-width: 640px;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, .15);
  padding: 16px;
}

.dialog-header {
  border-bottom: 1px solid var(--border);
  padding-bottom: 4px;
  margin-bottom: 20px;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field {
  margin-bottom: 1rem;
}

.field label {
  display: block;
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
  margin-bottom: 4px;
  font-weight: 600;
}

.field input[type="text"],
.field textarea,
.field select {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  padding: 8px;
  font-size: 14px;
  line-height: 20px;
}

.field input[type="text"]:focus-visible,
.field textarea:focus-visible,
.field select:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

.help {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
  padding-left: 10px;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
</style>
